import { API_CONFIG } from "../config/api.config";
import { apiRequest } from "./apiUtils";

// 数据类型定义
export interface ElderInfo {
  name: string;
  gender: number; // 1: 男性, 2: 女性, 3: 未知
  age: number;
  identity: number; // 身份枚举值
  identityName: string;
}

export interface AssignedStaff {
  name: string;
  phone: string;
}

export interface LastPaidVisitOrder {
  orderId: number;
  paidTime: string;
  productId: number;
  productName: string;
  productPriceFen: number;
}

export interface ElderVisitRelationItem {
  id: number;
  customerName: string;
  customerPhone: string;
  elderInfo: ElderInfo[];
  elderPhone: string;
  elderAddress: string;
  serviceCount: number;
  reportCount: number;
  assignedStaff?: AssignedStaff | null;
  visitPlan: number[];
  lastPaidVisitOrder?: LastPaidVisitOrder | null;
}

export interface ElderVisitRelationSearchParams {
  childName?: string;
  childPhone?: string;
  elderName?: string;
  elderGender?: "male" | "female"; // 前端使用字符串，后端会转换
  elderPhone?: string;
  elderAddress?: string;
  staffName?: string;
  elderAgeMin?: number;
  elderAgeMax?: number;
  page?: number;
  pageSize?: number;
}

export interface ElderVisitRelationListResponse {
  success: boolean;
  data: {
    items: ElderVisitRelationItem[];
    meta: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
      hasNext: boolean;
      hasPrevious: boolean;
    };
  };
  timestamp: string;
}

export interface AssignElderVisitRelationRequest {
  orderId: number;
  staffId: number;
}

export interface AssignElderVisitRelationResponse {
  success: boolean;
  data: {
    success: boolean;
    message: string;
    orderId: number;
    assignedStaff: AssignedStaff;
  };
  timestamp: string;
}

// 增值服务分配相关类型定义
export interface ValueAddedServiceOrder {
  orderId: number;
  customerName: string;
  customerPhone: string;
  elderInfo: ElderInfo[];
  elderPhone: string;
  elderAddress: string;
  serviceType: string;
  serviceTypeId: number;
  productName: string;
  productPriceFen: number;
  paidTime: string;
  assignedVisitStaff?: AssignedStaff | null; // 关联随访人员
  assignedServiceStaff?: AssignedStaff | null; // 关联服务人员
}

export interface ValueAddedServiceSearchParams {
  customerName?: string;
  customerPhone?: string;
  elderName?: string;
  elderGender?: "male" | "female";
  elderPhone?: string;
  elderAddress?: string;
  serviceTypeId?: number;
  page?: number;
  pageSize?: number;
}

export interface ValueAddedServiceListResponse {
  success: boolean;
  data: {
    items: ValueAddedServiceOrder[];
    meta: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
      hasNext: boolean;
      hasPrevious: boolean;
    };
  };
  timestamp: string;
}

export interface AssignValueAddedServiceRequest {
  orderId: number;
  staffId: number;
}

export interface AssignValueAddedServiceResponse {
  success: boolean;
  data: {
    success: boolean;
    message: string;
    orderId: number;
    assignedServiceStaff: AssignedStaff;
  };
  timestamp: string;
}

// 服务类型相关类型定义
export interface ServiceType {
  id: number;
  name: string;
  description?: string;
  imageUrl?: string;
  createdAt: string;
  updatedAt: string;
}

export interface ServiceTypesResponse {
  success: boolean;
  data: ServiceType[];
  timestamp: string;
}

/**
 * 长者信息接口（用于创建客户订单）
 */
export interface ElderData {
  name: string;
  gender: number;
  age: number;
  birthday: string;
  identity: number;
}

/**
 * 创建客户订单请求数据
 */
export interface CreateCustomerOrderRequest {
  // 客户信息
  customerName: string;
  customerGender: number;
  customerPhone: string;

  // 地址信息
  cityId: number;
  detailAddress: string;
  latitude: number;
  longitude: number;

  // 长者信息
  elderPhone: string;
  elderCount: number;
  elders: ElderData[];

  // 订单信息
  productId: number;
}

/**
 * 创建客户订单响应数据
 */
export interface CreateCustomerOrderResponse {
  customerId: number;
  elderRegistrationId: number;
  orderId: number;
  orderAmountFen: number;
  orderAmountYuan: number;
}

/**
 * 客户管理 API 服务
 * 使用公共的 apiRequest 方法进行统一的API调用
 */
export class CustomerService {
  /**
   * 创建客户订单
   */
  static async createCustomerOrder(
    data: CreateCustomerOrderRequest
  ): Promise<CreateCustomerOrderResponse> {
    try {
      const response = await apiRequest<{ data: CreateCustomerOrderResponse }>(
        "/operation-api/v1/customer-management/create-customer-order",
        "POST",
        data,
        true
      );
      return response.data;
    } catch (error) {
      console.error("创建客户订单失败:", error);
      throw new Error("创建客户订单失败，请重试");
    }
  }

  /**
   * 搜索老人随访关系列表
   */
  static async searchElderVisitRelations(
    params: ElderVisitRelationSearchParams
  ): Promise<ElderVisitRelationListResponse> {
    try {
      const response = await apiRequest<ElderVisitRelationListResponse>(
        API_CONFIG.ENDPOINTS.ELDER_VISIT_RELATIONS,
        "GET",
        params,
        true
      );
      return response;
    } catch (error) {
      console.error("搜索老人随访关系失败:", error);
      throw new Error("搜索老人随访关系失败，请重试");
    }
  }

  /**
   * 分配老人随访关系
   */
  static async assignElderVisitRelation(
    request: AssignElderVisitRelationRequest
  ): Promise<AssignElderVisitRelationResponse> {
    try {
      const response = await apiRequest<AssignElderVisitRelationResponse>(
        API_CONFIG.ENDPOINTS.ASSIGN_ELDER_VISIT_RELATION,
        "POST",
        request,
        true
      );
      return response;
    } catch (error) {
      console.error("分配老人随访关系失败:", error);
      throw new Error("分配老人随访关系失败，请重试");
    }
  }

  /**
   * 搜索增值服务订单列表
   */
  static async searchValueAddedServiceOrders(
    params: ValueAddedServiceSearchParams
  ): Promise<ValueAddedServiceListResponse> {
    try {
      const response = await apiRequest<ValueAddedServiceListResponse>(
        API_CONFIG.ENDPOINTS.VALUE_ADDED_SERVICE_ORDERS,
        "GET",
        params,
        true
      );
      return response;
    } catch (error) {
      console.error("搜索增值服务订单失败:", error);
      throw new Error("搜索增值服务订单失败，请重试");
    }
  }

  /**
   * 分配增值服务人员
   */
  static async assignValueAddedService(
    request: AssignValueAddedServiceRequest
  ): Promise<AssignValueAddedServiceResponse> {
    try {
      const response = await apiRequest<AssignValueAddedServiceResponse>(
        API_CONFIG.ENDPOINTS.ASSIGN_VALUE_ADDED_SERVICE,
        "POST",
        request,
        true
      );
      return response;
    } catch (error) {
      console.error("分配增值服务人员失败:", error);
      throw new Error("分配增值服务人员失败，请重试");
    }
  }

  /**
   * 获取所有服务类型（排除随访服务）
   */
  static async getServiceTypes(): Promise<ServiceTypesResponse> {
    try {
      const response = await apiRequest<ServiceTypesResponse>(
        API_CONFIG.ENDPOINTS.SERVICE_TYPES,
        "GET",
        undefined,
        true
      );
      return response;
    } catch (error) {
      console.error("获取服务类型失败:", error);
      throw new Error("获取服务类型失败，请重试");
    }
  }
}

export default CustomerService;
